import React, { useState, useEffect, useRef, useMemo } from 'react';
import './EducationModal.scss';
import useAnimatedModalClose from './useAnimatedModalClose';
import useModalFocusTrap from './useModalFocusTrap';
import useModalTouchSwipe from './useModalTouchSwipe';

const EDUCATION_OPTIONS = [
  { value: 'high school/ged', label: 'HIGH SCHOOL/GED' },
  { value: 'undergraduate', label: 'UNDERGRADUATE' },
  { value: 'graduate',      label: 'GRADUATE' },
  { value: 'vocational',    label: 'VOCATIONAL' },
];

const isOpenItem = item => item?.closed === false
  || (typeof item?.closed === 'string' && item.closed.trim().toLowerCase() === 'false');

  function EducationModal({ data, onApply, onClose, currentValue, onResetAll, onBack }) {
      const { isClosing, requestClose } = useAnimatedModalClose(onClose);
    const filterName = 'Education';
  const [selected, setSelected] = useState(currentValue || '');
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const { swipeOffset, isSwiping, swipedDownRef, touchHandlers } = useModalTouchSwipe({ modalRef, requestClose });

  useModalFocusTrap({
    modalRef,
    requestClose,
    initialFocusRef: closeRef,
    initialFocusSelector: onBack ? '.edu-modal-back' : undefined,
  });

  // Track if modal was opened from filter-trigger-btn (no onBack)
  const [openedFromTrigger, setOpenedFromTrigger] = useState(!onBack);
  useEffect(() => {
    setOpenedFromTrigger(!onBack);
    if (!onBack) {
      window.__modalOpenedFromTrigger = true;
    } else {
      window.__modalOpenedFromTrigger = false;
    }
  }, [onBack]);
  const resultCount = useMemo(() => {
    if (!selected) return 0;

    return (data || []).filter(item => {
      if (!isOpenItem(item)) return false;

      const levels = Array.isArray(item.academicLevel)
        ? item.academicLevel
        : (item.academicLevel ? [item.academicLevel] : []);

      return levels.some(level => level.toLowerCase() === selected.toLowerCase());
    }).length;
  }, [data, selected]);
  const applyLabel = selected ? `VIEW RESULTS (${resultCount})` : 'VIEW RESULTS';

  const handleApply = () => {
    onApply(selected);
    requestClose();
  };

  const handleBack = () => {
    requestClose(onBack);
  };

    const handleReset = () => {
      setSelected('');
      if (typeof onResetAll === 'function') onResetAll();
    };

  // Animation class for stacked modals
  const stackedAnimClass = onBack && !isClosing ? ' stacked-slide-in'
    : (isClosing && onBack && !swipedDownRef.current ? ' stacked-slide-out' : '');
  const slowSlideClass = (openedFromTrigger || (isClosing && swipedDownRef.current))
    ? (isClosing ? ' slow-slide-down' : ' slow-slide-up')
    : '';
  return (
    <div
      className={`edu-modal-overlay${isClosing ? ' closing' : ''}`}
      role="presentation"
      onClick={e => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        className={`edu-modal${isClosing ? ' closing' : ''}${stackedAnimClass}${slowSlideClass}${isSwiping ? ' is-swiping' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edu-modal-title"
        ref={modalRef}
        style={{
          '--swipe-offset': `${swipeOffset}px`,
          '--swipe-opacity': swipeOffset > 0 ? String(Math.max(0.25, 1 - swipeOffset / 420)) : '1',
        }}
      >
        <button className="edu-modal-handle-btn" onClick={requestClose} aria-label="Close modal" type="button" />
        <div
          className="edu-modal-header"
          onTouchStart={touchHandlers.onTouchStart}
          onTouchMove={touchHandlers.onTouchMove}
          onTouchEnd={touchHandlers.onTouchEnd}
        >
          {onBack ? (
            <button
              className="edu-modal-back"
              onClick={handleBack}
              aria-label="Back to all filters"
              type="button"
            >
              <span aria-hidden="true">&lt;</span>
            </button>
          ) : <span className="edu-modal-back-spacer" aria-hidden="true" />}
          <h2 id="edu-modal-title" className="edu-modal-title">Choose {filterName}</h2>
          <button
            className="edu-modal-close"
            onClick={requestClose}
            aria-label="Close education filter"
            ref={closeRef}
            type="button"
          >
            <span aria-hidden="true">&#10005;</span>
          </button>
        </div>

        <fieldset className="edu-modal-fieldset">
          <legend className="sr-only">Education level</legend>
          {EDUCATION_OPTIONS.map(opt => (
            <label key={opt.value} className="edu-modal-option">
              <span className="edu-modal-option-label">{opt.label}</span>
              <input
                type="radio"
                name="education-level"
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => { setSelected(opt.value); }}
              />
            </label>
          ))}
        </fieldset>

        <div className="edu-modal-actions">
          <button
            className="edu-modal-apply"
            onClick={handleApply}
            disabled={!selected}
          >
            {applyLabel}
          </button>
          <button className="edu-modal-reset" onClick={handleReset}>
			CLEAR FILTERS
          </button>
        </div>
      </div>
    </div>
  );
}

export default EducationModal;
